import { useEffect, useState } from "react";
import { FileRecord } from "@/models/club";
import { fetchMerchantImage } from "@/services/file";

interface UseGetImageProps {
  image?: FileRecord;
  bucket?: string;
}

const useGetImage = ({ image, bucket }: UseGetImageProps) => {
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Reset state when image changes
    setImageUrl(null);
    setError(null);

    // If no fileName, exit early
    if (!image?.fileName) {
      setIsLoading(false);
      return;
    }

    let isMounted = true;
    let currentUrl: string | null = null;
    setIsLoading(true);

    (async () => {
      try {
        const url = await fetchMerchantImage(image.fileName!, bucket);
        currentUrl = url;
        if (isMounted) {
          setImageUrl(url);
          setError(null);
        }
      } catch (err) {
        console.error("Failed to fetch image:", err);
        if (isMounted) {
          setError(
            err instanceof Error ? err.message : "Failed to fetch image"
          );
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    })();

    // Clean up URL when unmounted or when image changes
    return () => {
      isMounted = false;
      if (currentUrl) {
        URL.revokeObjectURL(currentUrl);
      }
    };
  }, [image?.fileName]);

  return { imageUrl, isLoading, error, setImageUrl };
};

export default useGetImage;
